import React, {
  createContext,
  useCallback,
  useState,
  useMemo,
  useContext,
  PropsWithChildren,
  useEffect,
} from "react";
import { useGetBranchesFromDb } from "@/api/pizza-checker/biteberry/biteberry";
import { useIdentity } from "@/context/identity-context";
import {
  readLocalStorageState,
  removeLocalStorageState,
  writeLocalStorageState,
} from "@/hooks/use-local-storage-state";

export interface Branch {
  id: string;
  name: string;
  enable_acquisition: boolean;
  enable_bb_trigger: boolean;
  enable_evaluation: boolean;
  bb_branch_id: string;
  latitude?: number;
  longitude?: number;
  enabled?: boolean;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

export interface BranchesData {
  branches: Branch[];
}

type RawBranch = {
  _id?: string;
  id?: string;
  name?: string;
  title?: string;
  enable_acquisition?: boolean;
  enable_bb_trigger?: boolean;
  enable_evaluation?: boolean;
  bb_branch_id?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  latitude?: unknown;
  lat?: unknown;
  gps_latitude?: unknown;
  longitude?: unknown;
  lng?: unknown;
  lon?: unknown;
  gps_longitude?: unknown;
  coordinates?: unknown;
  location?: unknown;
  geo?: unknown;
  gps?: unknown;
  address?: unknown;
  position?: unknown;
};

type BranchUsageCounts = Record<string, number>;

interface BranchContextType {
  branches: Branch[] | null;
  selectedBranch: Branch | null;
  selectedBranches: Branch[];
  selectedBranchId: string | null;
  selectedBranchIds: string[];
  branchUsageCounts: BranchUsageCounts;
  branchesLoading: boolean;
  canSelectBranches: boolean;
  selectBranch: (branchId: string | null) => void;
  selectBranches: (branchIds: string[]) => void;
  getBranchById: (branchId: string) => Branch | undefined;
  refetchBranches: () => void;
}

const BranchContext = createContext<BranchContextType | null>(null);
const SELECTED_BRANCH_STORAGE_KEY = "selectedBranchId";
const SELECTED_BRANCHES_STORAGE_KEY = "selectedBranchIds";
const BRANCH_USAGE_STORAGE_KEY = "pizza-admin:branch-usage-counts:v1";

const isSameSelection = (a: string[], b: string[]) =>
  a.length === b.length && a.every((item, index) => item === b[index]);

const deserializeBranchIds = (
  storedValue: unknown,
  fallback: string[],
): string[] => {
  if (Array.isArray(storedValue)) {
    return storedValue.filter((item): item is string => typeof item === "string");
  }

  if (typeof storedValue === "string" && storedValue.trim()) {
    return [storedValue];
  }

  return fallback;
};

const deserializeBranchId = (
  storedValue: unknown,
  fallback: string | null,
): string | null =>
  typeof storedValue === "string" && storedValue.trim()
    ? storedValue
    : fallback;

const dedupeBranchIds = (branchIds: string[]) =>
  Array.from(new Set(branchIds.filter((branchId) => branchId.trim())));

const deserializeBranchUsageCounts = (
  storedValue: unknown,
  fallback: BranchUsageCounts,
): BranchUsageCounts => {
  if (typeof storedValue !== "object" || storedValue === null) {
    return fallback;
  }

  return Object.fromEntries(
    Object.entries(storedValue)
      .filter((entry): entry is [string, number] =>
        typeof entry[1] === "number" && Number.isFinite(entry[1]),
      )
      .map(([branchId, count]) => [branchId, Math.max(0, count)]),
  );
};

const readNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const readCoordinatePair = (record: Record<string, unknown>) => {
  const latitude =
    readNumber(record.latitude) ??
    readNumber(record.lat) ??
    readNumber(record.gps_latitude);
  const longitude =
    readNumber(record.longitude) ??
    readNumber(record.lng) ??
    readNumber(record.lon) ??
    readNumber(record.gps_longitude);

  if (latitude !== undefined && longitude !== undefined) {
    return { latitude, longitude };
  }

  return undefined;
};

const extractCoordinates = (value: unknown): Pick<
  Branch,
  "latitude" | "longitude"
> => {
  if (!isRecord(value)) {
    return {};
  }

  const directPair = readCoordinatePair(value);
  if (directPair) {
    return directPair;
  }

  if (Array.isArray(value.coordinates) && value.coordinates.length >= 2) {
    const longitude = readNumber(value.coordinates[0]);
    const latitude = readNumber(value.coordinates[1]);

    if (latitude !== undefined && longitude !== undefined) {
      return { latitude, longitude };
    }
  }

  for (const key of ["location", "geo", "gps", "address", "position"]) {
    const nestedCoordinates = extractCoordinates(value[key]);
    if (
      nestedCoordinates.latitude !== undefined &&
      nestedCoordinates.longitude !== undefined
    ) {
      return nestedCoordinates;
    }
  }

  return {};
};

export const BranchProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { identity } = useIdentity();
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>(() => {
    const storedBranchIds = readLocalStorageState<string[]>(
      SELECTED_BRANCHES_STORAGE_KEY,
      [],
      deserializeBranchIds,
    );

    if (storedBranchIds.length > 0) {
      return storedBranchIds;
    }

    const legacyBranchId = readLocalStorageState<string | null>(
      SELECTED_BRANCH_STORAGE_KEY,
      null,
      deserializeBranchId,
    );

    return legacyBranchId ? [legacyBranchId] : [];
  });
  const [branchUsageCounts, setBranchUsageCounts] =
    useState<BranchUsageCounts>(() =>
      readLocalStorageState<BranchUsageCounts>(
        BRANCH_USAGE_STORAGE_KEY,
        {},
        deserializeBranchUsageCounts,
      ),
    );

  const {
    data,
    isLoading: branchesLoading,
    refetch,
  } = useGetBranchesFromDb<BranchesData>({
    query: {
      enabled: !!identity?.token,
    },
  });

  const { branches } = useMemo(
    () => ({
      branches:
        data?.branches.map((branch: RawBranch): Branch => {
          const coordinates = extractCoordinates(branch);

          return {
            id: branch._id || branch.id || branch.bb_branch_id || "",
            name: branch.name || branch.title || "Unnamed Branch",
            enable_acquisition: branch.enable_acquisition ?? false,
            enable_bb_trigger: branch.enable_bb_trigger ?? true,
            enable_evaluation: branch.enable_evaluation ?? false,
            enabled:
              (branch.enable_acquisition || branch.enable_evaluation) ?? false,
            bb_branch_id: branch.bb_branch_id || "",
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
            created_at: branch.created_at || branch.createdAt,
            updated_at: branch.updated_at || branch.updatedAt,
          };
        }) || [],
    }),
    [data],
  );

  const selectedBranches = useMemo(() => {
    if (!branches || selectedBranchIds.length === 0) {
      return [];
    }

    return selectedBranchIds
      .map((branchId) => branches.find((branch) => branch.id === branchId))
      .filter((branch): branch is Branch => Boolean(branch));
  }, [branches, selectedBranchIds]);

  const selectedBranch = selectedBranches[0] ?? null;
  const selectedBranchId = selectedBranch?.id ?? null;

  const tokenBranchId = identity?.payload?.branch_id;
  const hasFixedBranch =
    tokenBranchId !== "None" &&
    tokenBranchId !== "null" &&
    tokenBranchId !== null &&
    tokenBranchId !== undefined &&
    tokenBranchId !== "";
  const canSelectBranches = !hasFixedBranch;

  useEffect(() => {
    if (!identity?.payload || !branches || branches.length === 0) {
      return;
    }

    if (hasFixedBranch) {
      const matchingBranch = branches.find(
        (branch) => branch.bb_branch_id === tokenBranchId,
      );

      if (matchingBranch) {
        setSelectedBranchIds((current) =>
          isSameSelection(current, [matchingBranch.id])
            ? current
            : [matchingBranch.id],
        );
      }

      return;
    }

    setSelectedBranchIds((current) => {
      const validBranchIds = current.filter((branchId) =>
        branches.some((branch) => branch.id === branchId),
      );

      return isSameSelection(current, validBranchIds) ? current : validBranchIds;
    });
  }, [branches, hasFixedBranch, identity?.payload, tokenBranchId]);

  useEffect(() => {
    if (selectedBranchIds.length > 0) {
      writeLocalStorageState(SELECTED_BRANCHES_STORAGE_KEY, selectedBranchIds);
      writeLocalStorageState(SELECTED_BRANCH_STORAGE_KEY, selectedBranchIds[0]);
      return;
    }

    removeLocalStorageState(SELECTED_BRANCHES_STORAGE_KEY);
    removeLocalStorageState(SELECTED_BRANCH_STORAGE_KEY);
  }, [selectedBranchIds]);

  useEffect(() => {
    writeLocalStorageState(BRANCH_USAGE_STORAGE_KEY, branchUsageCounts);
  }, [branchUsageCounts]);

  const recordBranchUsage = useCallback(
    (branchIds: string[]) => {
      const usedBranchIds = dedupeBranchIds(branchIds);
      if (usedBranchIds.length === 0) {
        return;
      }

      if (branches && usedBranchIds.length === branches.length) {
        return;
      }

      setBranchUsageCounts((current) => {
        const next = { ...current };
        for (const branchId of usedBranchIds) {
          next[branchId] = (next[branchId] ?? 0) + 1;
        }
        return next;
      });
    },
    [branches],
  );

  const selectBranch = useCallback((branchId: string | null) => {
    if (!canSelectBranches) {
      return;
    }

    if (branchId) {
      recordBranchUsage([branchId]);
    }
    setSelectedBranchIds(branchId ? [branchId] : []);
  }, [canSelectBranches, recordBranchUsage]);

  const selectBranches = useCallback((branchIds: string[]) => {
    if (!canSelectBranches) {
      return;
    }

    const nextBranchIds = dedupeBranchIds(branchIds);
    const newlySelectedBranchIds = nextBranchIds.filter(
      (branchId) => !selectedBranchIds.includes(branchId),
    );

    recordBranchUsage(newlySelectedBranchIds);
    setSelectedBranchIds(nextBranchIds);
  }, [canSelectBranches, recordBranchUsage, selectedBranchIds]);

  const getBranchById = useCallback((branchId: string): Branch | undefined => {
    return branches?.find((branch) => branch.id === branchId);
  }, [branches]);

  const refetchBranches = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <BranchContext.Provider
      value={useMemo(
        () => ({
          branches,
          selectedBranch,
          selectedBranches,
          selectedBranchId,
          selectedBranchIds,
          branchUsageCounts,
          branchesLoading,
          canSelectBranches,
          selectBranch,
          selectBranches,
          getBranchById,
          refetchBranches,
        }),
        [
          branches,
          selectedBranch,
          selectedBranches,
          selectedBranchId,
          selectedBranchIds,
          branchUsageCounts,
          branchesLoading,
          canSelectBranches,
          selectBranch,
          selectBranches,
          getBranchById,
          refetchBranches,
        ],
      )}
    >
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error("useBranch must be used within a BranchProvider");
  }
  return context;
};
